<template>
  <div
    class="web-slider"
    :class="{
      'web-slider--full': normalizedMode === 'full',
      'web-slider--container': normalizedMode === 'container',
    }"
    :style="sliderOuterStyle"
    @click="carouselClick"
  >
    <div class="web-slider__inner" :style="sliderInnerStyle">
      <b-carousel
        :id="carouselId || 'carousel-1'"
        :interval="5000"
        :indicators="sliders.length > 1"
        :controls="sliders.length > 1"
        img-width="1024"
        img-height="480"
        class="web-slider__carousel"
      >
        <b-carousel-slide
          v-for="(slider, i) in sliders"
          :key="`slider-${i}`"
          :img-src="hasImage(slider) ? slider.src : undefined"
          :img-blank="!hasImage(slider)"
          :img-blank-color="getSlideBackgroundColor(slider)"
          class="click web-slider__slide"
          :class="{ 'web-slider__slide--text-only': !hasImage(slider) }"
        >
          <div
            class="web-slider__content"
            :class="`web-slider__content--${getSlideTextAlign(slider)}`"
            :style="{
              color: getSlideTextColor(slider),
              textShadow: hasImage(slider) ? '1px 1px 2px #333' : 'none',
            }"
          >
            <h2
              v-if="slider.caption"
              class="web-slider__title"
              :style="getSlideElementStyle(slider, 'title')"
            >
              {{ slider.caption }}
            </h2>
            <p
              v-if="slider.text"
              class="web-slider__text"
              :style="getSlideElementStyle(slider, 'text')"
            >
              {{ slider.text }}
            </p>
            <router-link
              class="btn px-5"
              :class="[
                hasImage(slider) ? 'btn-outline-light' : 'btn-light',
                { 'web-slider__button--custom-color': hasSlideButtonColor(slider) },
              ]"
              :style="getSlideElementStyle(slider, 'button')"
              v-if="slider.to"
              :to="getLink(slider.to)"
            >
              {{ slider.buttonLabel || "VER" }}
            </router-link>
          </div>
        </b-carousel-slide>
      </b-carousel>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    sliders: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: "container",
    },
    padding: {
      type: Number,
      default: 0,
    },
    carouselId: {
      type: String,
      default: "",
    },
  },
  computed: {
    normalizedMode() {
      return this.mode === "full" ? "full" : "container";
    },
    normalizedPadding() {
      return Math.max(0, Number(this.padding || 0));
    },
    sliderOuterStyle() {
      if (this.normalizedMode === "full") {
        return {
          paddingLeft: `${this.normalizedPadding}px`,
          paddingRight: `${this.normalizedPadding}px`,
        };
      }

      return {
        paddingLeft: `${this.normalizedPadding}px`,
        paddingRight: `${this.normalizedPadding}px`,
      };
    },
    sliderInnerStyle() {
      if (this.normalizedMode === "container") {
        return {
          maxWidth: "1300px",
          margin: "0 auto",
        };
      }

      return {};
    },
  },
  methods: {
    getSlideElementStyle(slider = {}, element) {
      const fields = {
        title: ["titleCss", "titleColor", "titleFontSize", "8vw"],
        text: ["textCss", "textColor", "textFontSize", "4.5vw"],
        button: ["buttonCss", "buttonTextColor", "buttonFontSize", "4vw"],
      };
      const [cssField, colorField, sizeField, mobileLimit] = fields[element];
      const style = this.parseInlineCss(slider[cssField]);
      const cssColor = String(style.color || "").replace(/\s*!important\s*$/i, "").trim();
      delete style.color;
      const color = String(slider[colorField] || "").trim();
      const resolvedColor = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)
        ? color
        : cssColor;
      if (resolvedColor) style[`--web-slider-${element}-color`] = resolvedColor;

      const size = Number(slider[sizeField]);
      if (Number.isFinite(size) && size >= 10) {
        style.fontSize = `min(${Math.min(size, 120)}px, ${mobileLimit})`;
      }
      return style;
    },
    hasSlideButtonColor(slider = {}) {
      return Boolean(this.getSlideElementStyle(slider, "button")["--web-slider-button-color"]);
    },
    getSlideTextAlign(slider = {}) {
      return ["left", "center", "right"].includes(slider.textAlign)
        ? slider.textAlign
        : "center";
    },
    hasImage(slider = {}) {
      return Boolean(String(slider.src || "").trim());
    },
    getSlideBackgroundColor(slider = {}) {
      const color = String(slider.bgColor || "").trim();
      if (!color || color.toLowerCase() === "transparent") return "#ffffff";
      return color;
    },
    getSlideTextColor(slider = {}) {
      if (this.hasImage(slider)) return "#ffffff";
      const color = this.getSlideBackgroundColor(slider).replace("#", "");
      if (!/^[0-9a-f]{6}$/i.test(color)) return "#20313f";
      const r = parseInt(color.slice(0, 2), 16);
      const g = parseInt(color.slice(2, 4), 16);
      const b = parseInt(color.slice(4, 6), 16);
      const luminance = (r * 299 + g * 587 + b * 114) / 1000;
      return luminance > 160 ? "#20313f" : "#ffffff";
    },
    parseInlineCss(value = "") {
      return String(value || "")
        .split(";")
        .map((entry) => entry.trim())
        .filter(Boolean)
        .reduce((styles, rule) => {
          const separatorIndex = rule.indexOf(":");
          if (separatorIndex === -1) return styles;

          const property = rule.slice(0, separatorIndex).trim();
          const cssValue = rule.slice(separatorIndex + 1).trim();
          if (!property || !cssValue) return styles;

          const camelProperty = property.replace(/-([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          );
          styles[camelProperty] = cssValue;
          return styles;
        }, {});
    },
    carouselClick() {},
  },
};
</script>

<style scoped>
.web-slider {
  width: 100%;
  margin-bottom: 1rem;
}

.web-slider--full {
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.web-slider__inner {
  width: 100%;
}

.web-slider__carousel {
  background: transparent;
}

.web-slider__slide--text-only {
  background: transparent;
}

.web-slider__content {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: clamp(0.75rem, 2.5vw, 2rem) clamp(1rem, 10vw, 10rem);
}

.web-slider__content--left {
  align-items: flex-start;
  text-align: left;
}

.web-slider__content--right {
  align-items: flex-end;
  text-align: right;
}

.web-slider__title {
  color: var(--web-slider-title-color, inherit) !important;
  font-size: clamp(1rem, 2.8vw, 3.5rem);
  font-weight: 800;
  margin-bottom: clamp(0.2rem, 1vw, 0.75rem);
}

.web-slider__text {
  color: var(--web-slider-text-color, inherit) !important;
  font-size: clamp(0.8rem, 1.3vw, 1.35rem);
  margin-bottom: clamp(0.3rem, 1.5vw, 1.25rem);
  max-width: 720px;
}

.web-slider__button--custom-color {
  color: var(--web-slider-button-color) !important;
}
</style>

<style>
.web-slider .carousel-caption {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0;
}
</style>
